import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useImmer } from "use-immer";
import { InView } from "react-intersection-observer";
import PLink from "../../common/Navigation/PLink";
import LEAD_FIELDS, { DEFAULT_VISIBLE_FIELDS } from "../../utils/leadFields";
import { useDebouncedCallback } from "use-debounce";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { useLeadsInfinite } from "../../hooks/useLeads";
import useCategories from "../../hooks/useCategories";
import useMutateLead from "../../hooks/useMutateLead";
import useBusinesses from "../../hooks/useBusinesses";
import useGlobalParams from "../../hooks/useGlobalParams";
import useUserCategoryColumns from "../../hooks/useUserCategoryColumns";
import useStatuses from "../../hooks/useStatuses";
import useToast from "../../hooks/useToast";
import { capitalize } from "../../utils/userUtils";
import { leadFullName, leadInitials, statusBG, statusColor } from "../../utils/leadUtils";

import Toast from "../../common/Toast/Toast";
import LeadsMode from "../../common/Navigation/LeadsMode";
import Form from 'react-bootstrap/Form';
import Dropdown from "react-bootstrap/Dropdown";
import { Trans as T } from 'react-i18next';

import FilterDropdown from "./FilterDropdown";
import SortableColumn from "./SortableColumn";
import LeadRow from "./LeadRow";
import FilterBar from "./FilterBar";
import LeadDetailsModal from "./LeadDetailsModal";
import EditColumnsDropdown from "./EditColumnsDropdown";
import ExportModal from "./ExportModal";
import ImportLeadsModal from "../../common/Leads/ImportLeadsModal";
import LeadImportNotifications from "../../common/Leads/LeadImportNotifications";
import BulkEditModal from "./BulkEditModal";
import LeadRangeDropdown from "./LeadRangeDropdown";

import FilterIcon from "../../assets/icons/FilterIcon";
import LeadsIcon from "../../assets/icons/LeadsIcon";
import TrashIcon from "../../assets/icons/TrashIcon";
import { ReactComponent as ExportIcon } from "../../assets/icons_svg/export.svg";
import { ReactComponent as ImportIcon } from "../../assets/icons_svg/import.svg";
import { ReactComponent as LightningIcon } from "../../assets/icons_svg/lightning.svg";
import { ReactComponent as ThreeDotsIcon } from "../../assets/icons_svg/three_dots.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons_svg/search-icon.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons_svg/calendar.svg";

import '../../common/Dropdown/Dropdown.scss';
import './LeadsListView.scss';
import { DATE_RANGE_TYPES } from "../../utils/dates";

const BULK_ACTION_WIDTH = 40;
const DELETE_WIDTH = 40;

export default function LeadsListView() {

  const { categoryId, organizationId, businessId } = useGlobalParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedLeads, setSelectedLeads] = useImmer(() => new Set());
  const [sortBy, setSortBy] = useState('date_added');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useImmer([]);
  const [dateRange, setDateRange] = useState(null);
  const [editedLeads, setEditedLeads] = useImmer(() => new Set());
  const [showDetails, setShowDetails] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [undoItems, setUndoItems] = useImmer();
  const [showUserColumns, setShowUserColumns] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkdEditType, setBulkEditType] = useState(null);
  const [toastAction, setToastAction] = useState(null);
  const [highlightIds, setHighlightIds] = useState(null);

  const { toast, showToast, hideToast } = useToast({ duration: 5000 });

  const showFilters = filters.length > 0;

  const ordering = (sortDir === 'desc' ? '-' : '') + sortBy;

  const { data: businesses } = useBusinesses({ organization_id: organizationId, staleTime: 1000 });
  const { data: leads, isLoading, isFetching, isError, fetchNextPage, isFetchingNextPage, hasNextPage } = useLeadsInfinite({ staleTime: 15000, ordering, filters, categoryId, editedLeads, dateRange: dateRange?.dates });
  const { data: categories } = useCategories({ businessId, staleTime: 10000, keepPreviousData: true });
  const { data: categoryColumns } = useUserCategoryColumns({ categoryId });
  const { data: statuses } = useStatuses({ categoryId });

  const { deleteMutation, bulkDeleteMutation, undoDeleteMutation, bulkUndoDeleteMutation } = useMutateLead({ categoryId });

  const business = businesses?.find(business => business.id === businessId);
  const category = categories?.find(category => category.id === categoryId);

  const columns = useMemo(() => {
    return categoryColumns?.[0]?.columns.filter(item => item.visible).map(item => item.column) || DEFAULT_VISIBLE_FIELDS;
  }, [categoryColumns]);

  const minTableWidth = useMemo(() => {
    return columns.reduce((acc, column) => acc + (LEAD_FIELDS[column]?.columnWidth || 120), 0);
  }, [columns]);

  const orderedStatuses = useMemo(() => {
    return statuses?.sort((a, b) => a.order_index - b.order_index);
  }, [statuses]);


  useEffect(() => {
    setFilters([]);
    setSortBy('date_added');
    setEditedLeads(() => new Set());

    // removing them will cause the skeleton rows to show
    // queryClient.removeQueries(['leads', categoryId]);

    // on categoryId change, keep only the first page, to avoid refetching all pages and cursor URLs:
    queryClient.setQueriesData(['leads', categoryId], data => {
      if (!data) {
        return undefined;
      }

      return {
        ...data,
        pages      : data.pages.slice(0, 1),
        pageParams : data.pageParams.slice(0, 1),
      }
    });
  }, [categoryId, setFilters, setEditedLeads, queryClient]);

  // if filters change, unselect leads that are no longer visible:
  useEffect(() => {
    if (selectedLeads?.size > 0 && leads) {
      const newSelection = [];

      leads.pages.forEach(page => {
        page.data.forEach(lead => {

          if (selectedLeads.has(lead.id)) {
            newSelection.push(lead.id);
          }
        });
      });

      if (selectedLeads.size > newSelection.length) {
        setSelectedLeads(new Set(newSelection));
      }
    }
  }, [leads, selectedLeads, setSelectedLeads]);

  const handleToggleLead = (lead, e) => {
    setSelectedLeads(draft => {
      e.target.checked ? draft.add(lead.id) : draft.delete(lead.id)
    });
  }

  const handleBulkDelete = () => {
    const ids = Array.from(selectedLeads.keys());
    bulkDeleteMutation.mutate(ids);

    const draftItems = [];
    setSelectedLeads(new Set());

    for (let id of ids) {
      leads.pages.some((page, pageIndex) => {
        const index = page.data.findIndex(lead => lead.id === id);

        if (index !== -1) {
          draftItems.push({ lead: page.data[index], index, pageIndex });
          return true;
        }

        return false;
      });
    }
    
    setUndoItems(draftItems.sort((a, b) => a.index - b.index));

    if (draftItems.length === 1) {
      showToast('Lead moved to trash', 5000);
      setToastAction('undo-delete');
    }
    else {
      showToast(`${draftItems?.length} leads moved to trash`, 5000);
      setToastAction('undo-bulk-delete');
    }
  }

  const handleUndo = () => {
    undoDeleteMutation.mutate(undoItems[0]);
    onHighlight([undoItems[0].lead.id]);
    setUndoItems(null);
  }

  const handleBulkDeleteUndo = () => {
    bulkUndoDeleteMutation.mutate(undoItems);
    setUndoItems(null);
    onHighlight(undoItems.map(item => item.lead.id));
  }

  const onHighlight = ids => {
    debouncedRemoveHighlights.cancel();
    setHighlightIds(ids);
    debouncedRemoveHighlights();
  }

  const debouncedRemoveHighlights = useDebouncedCallback(() => {
    setHighlightIds(null);
  }, [1000]);

  const handleDelete = lead => {
    // de-select the lead if it was selected:
    selectedLeads.has(lead.id) && setSelectedLeads(draft => {
      draft.delete(lead.id)
    });

    showToast('Lead moved to trash', 5000);
    setToastAction('undo-delete');

    showDetails && setShowDetails(false);

    leads.pages.some((page, pageIndex) => {
      const index = page.data.findIndex(l => l.id === lead.id);

      if (index !== -1) {
        setUndoItems([{ lead, index, pageIndex }]);
        return true;
      }

      return false;
    });

    deleteMutation.mutate(lead.id);
  }

  const handleChangeSort = (by, dir) => {
    setSortBy(by);
    setSortDir(dir); 
    (by !== sortBy || dir !== sortDir) && setEditedLeads(() => new Set());
  }

  const onSelectAll = e => {
    if (e.target.checked) {
      const allLeads = new Set(leads.pages.reduce((acc, cur) => [...acc, ...cur.data.map(lead => lead.id)], []));
      setSelectedLeads(allLeads);
    }
    else {
      setSelectedLeads(new Set());
    }
  }

  const onEditLead = (id, highlight) => {
    setEditedLeads(draft => {
      draft.add(id);
    });

    highlight && onHighlight([id]);
  }

  const onClickRow = id => {
    setEditingId(id);
    setShowDetails(true);
  }

  const onHideDetails = () => {
    setEditingId(null);
    setShowDetails(false);
  }

  const allLeadsSelected = useMemo(() => {
    const leadsCount = leads?.pages.reduce((acc, cur) => acc + cur.data.length, 0) || 0;
    return leadsCount > 0 && selectedLeads.size >= leadsCount;
  }, [leads, selectedLeads]);

  const leadsCount = useMemo(() => {
    return leads?.pages.reduce((acc, page) => acc + page.data.length, 0);
  }, [leads]);

  const editingLead = useMemo(() => {
    if (editingId) {
      for (let page of leads.pages) {
        const lead = page.data.find(lead => lead.id === editingId);

        if (lead) {
          return lead;
        }
      }
    }
  }, [editingId, leads]);

  const onBulkEdit = type => {
    setBulkEditType(type);
    setShowBulkEdit(true);
  }

  const onConfirmBulkEdit = () => {
    showToast("Leads edited successfully", 2000);
    setToastAction('bulk-update');
  }

  const onToastActionClick = () => {
    switch (toastAction) {
      case 'undo-delete':
        hideToast();
        return handleUndo();

      case 'undo-bulk-delete':
        hideToast();
        return handleBulkDeleteUndo();
        
      default:
        return;
    }
  }

  const getToastAction = () => {
    switch (toastAction) {
      case 'undo-delete':
      case 'undo-bulk-delete':
        return "Undo";

      default:
        return false;
    }
  }

  const changeCategory = e => {
    navigate({ pathname: `/leads/${e.target.value}`, search: location.search });
  }

  const columnFlex = column => (LEAD_FIELDS[column]?.columnWidth || 120) / minTableWidth;

  const renderSkeletonRow = () => (
    <div className='skeleton-row'>
      <div className='bulk-action' />
      { columns.map(column => (
        <div key={column} style={{ flex: (LEAD_FIELDS[column]?.columnWidth || 120) / minTableWidth, padding: 4 }}>
          <div className="skeleton-field" />
        </div>
      ))}
      <div className='delete-lead' />
    </div>
  );

  const renderInfiniteLoader = () => (
    <div className="infinite-loader">
      { renderSkeletonRow() }
      { renderSkeletonRow() }
      { renderSkeletonRow() }
    </div>
  );

  const renderTableBody = () => {
    if (isLoading && !leads?.pages) {
      return (
        <div className="infinite-loader">
          { renderSkeletonRow() }
          { renderSkeletonRow() }
          { renderSkeletonRow() }
          { renderSkeletonRow() }
          { renderSkeletonRow() }
          { renderSkeletonRow() }
          { renderSkeletonRow() }
        </div>
      );
    }
    else if (leadsCount === 0) {
      return;
    }

    else {
      return (
        <>
          { leads?.pages?.map(page => (
            <React.Fragment key={page.nextUrl}>
              { page.data.map(lead => (
                <LeadRow highlighted={highlightIds?.includes(lead.id)} key={lead.id} organizationId={organizationId} categoryId={categoryId} statuses={orderedStatuses} lead={lead} columns={columns} minTableWidth={minTableWidth} selected={selectedLeads.has(lead.id)} onEditLead={onEditLead} onToggleChange={e => handleToggleLead(lead, e)} onClickRow={onClickRow} onDeleteLead={() => handleDelete(lead)} />
              ))}
            </React.Fragment>
          ))}
          { hasNextPage && (
            !isFetchingNextPage
            ? <InView as="div" onChange={inView => inView && !isFetching && !isFetchingNextPage && fetchNextPage() }>
                { renderInfiniteLoader() }
              </InView>
            : renderInfiniteLoader()
            )
          }
      </>
      );
    }
  }

  const renderMobileLead = lead => {
    const status = statuses.find(status => status.id === lead.status_id);

    const now = DateTime.now();
    const date = DateTime.fromISO(lead.date_added);
    const addedToday = now.diff(date, 'days').days < 1;

    if (!status) {
      return;
    }

    return (
      <div className={classNames("lead-row-mobile", {'added-today': addedToday})}>
        <div className="lead-initials" style={{ color: statusColor(status), backgroundColor: statusBG(status, 0.2) }}>{ leadInitials(lead) }</div>
        <div className="lead-name-status">
          <div className="lead-name">{ leadFullName(lead) }</div>
          <div className="lead-status" style={{ color: statusColor(status), backgroundColor: statusBG(status) }}>{ status.name }</div>
        </div>
        <div className="lead-date">
          { now.diff(date, 'days').days < 7 ? capitalize(date.toRelativeCalendar()) : date.toLocaleString(DateTime.DATE_MED) }
        </div>
      </div>
    );
  }

  const renderMobileContent = () => {
    if (!statuses) {
      return;
    }

    const hasArchived = categories.some(category => category.archived);
    
    return (
      <>
        <div className="mobile-category-sort">
          <Form.Select value={category.id} onChange={changeCategory}>
            { categories.filter(category => !category.archived).map(category => (
              <option key={category.id} value={category.id}>{ category.name }</option>
            ))}
            { hasArchived && (
              <>
                <option value='disabled' disabled>Archived Categories</option>
                { categories.filter(category => category.archived).map(category => (
                  <option key={category.id} value={category.id}>{ category.name }</option>
                ))}
              </>
            )}
          </Form.Select>
        </div>
        <div className="lead-list-mobile">
          { leads?.pages?.map(page => page.data.map(lead => renderMobileLead(lead) ))}
        </div>
      </>
    )
  }

  const renderEmptyTable = () => {
    const clearFilter = () => setFilters([]);
    const clearRange = () => setDateRange(null);

    if (showFilters && dateRange) {
      return (
        <>
          <h3>No leads match the selected date range or filter</h3>
          <p><button className='clear-filter' onClick={clearFilter}>Clear filter</button> or <button className='clear-filter' onClick={clearRange}>clear the date range</button> to view other leads.</p> 
        </>
      );
    }
    else if (showFilters) {
      return (
        <>
          <h3>No leads match your filter</h3>
          <p><button className='clear-filter' onClick={clearFilter}>Clear filter</button> to view all leads</p> 
        </>
      );
    }
    else if (dateRange) {
      return (
        <>
          <h3>No leads match the selected date ranges</h3>
          <p><button className='clear-filter' onClick={clearRange}>Clear your date range</button> to view all your leads</p> 
        </>
      );
    }
    else {
      return (
        <>
          <h3>No leads in this category yet</h3>
          <p><button onClick={() => setShowDetails(true)}>Add a new lead</button>, <button onClick={() => setShowImport(true)}>import from CSV</button>, or <PLink to={`/leads/settings/${categoryId}`}>create an integration.</PLink></p>
        </>
      );
    }
  }

  const dateLabel = () => {
    if (dateRange?.type === 'custom') {
      const ranges = dateRange.dates;
      return <>{ DateTime.fromISO(ranges[0].value).toLocaleString(DateTime.DATE_MED) } - { DateTime.fromISO(ranges[1].value).toLocaleString(DateTime.DATE_MED) }</>
    }
    else {
      return <T>{ DATE_RANGE_TYPES[dateRange?.type] || 'All Time' }</T>;
    }
  }

  const renderContent = () => {
    if (isError) {
      return <pre>Error fetching leads :S </pre>
    }

    return (
      <>
        <div className='content'>
          <div className='controls'>
            <div className='title-category'>
              <div className='leads-icon'><LeadsIcon /></div>
              <div>
                <h2>{ business?.name }</h2>
                <div className='breadcrumbs'>
                  <div className={classNames('text-content', { 'all-leads': categories && categoryId })}>
                    <div>All Leads</div>
                    <div>Leads » { category?.name }</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='actions'>
              <button className='main-actions' onClick={() => setShowDetails(true)}>＋ <T>Add Lead</T></button>

              <FilterDropdown toggle={filters.length > 0 ? ActiveFilterToggle : FilterToggle} filters={filters} setFilters={setFilters} align='end'>
                <FilterIcon /><span><T>Filters</T></span>
              </FilterDropdown>

              <LeadRangeDropdown toggle={FilterToggle} dateRange={dateRange} setDateRange={setDateRange} earliestDate={category?.created_at}>
                <CalendarIcon /><span>{ dateLabel() }</span>
              </LeadRangeDropdown>

              { selectedLeads.size > 0 && (
                <Dropdown className='__dropdown actions-dropdown' align='end'>
                  <Dropdown.Toggle as={ActiveFilterToggle}>
                    <LightningIcon />
                    <span>Bulk Actions</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => onBulkEdit('status')}>Edit Status</Dropdown.Item>
                    <Dropdown.Item onClick={() => onBulkEdit('assignee')}>Edit Assignee</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleBulkDelete}><TrashIcon className='trash-icon' /> Delete Leads</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}

              <button className='main-actions search-action' onClick={() => alert("NOT DONE YET")}><SearchIcon /></button>

              <Dropdown className='__dropdown actions-dropdown' align='end'>
                <Dropdown.Toggle as={MoreToggle}>
                  <ThreeDotsIcon />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setShowImport(true)}><ImportIcon className='import-icon' />Import Leads</Dropdown.Item>
                  { leadsCount > 0 && <Dropdown.Item onClick={() => setShowExport(true)}><ExportIcon className='export-icon' />Export...</Dropdown.Item> }
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>

        { showFilters && <FilterBar filters={filters} setFilters={setFilters} removeFromTop={showUserColumns} /> }
        
        <div className={classNames('content', 'leads-table', { 'leads-table-empty': leadsCount === 0 && !isLoading, 'has-selection': selectedLeads?.size > 1 })}>
          { (leadsCount !== 0 || isLoading) && (
            <div className='table-overflow' style={{ width: minTableWidth + BULK_ACTION_WIDTH + DELETE_WIDTH }}>
              <div className='table-head'>
                <div className='bulk-action'>
                  <Form className="bulk-toggle-form">
                    <Form.Check type='checkbox' className='bulk-toggle' checked={allLeadsSelected} onChange={onSelectAll} />
                    { !allLeadsSelected && selectedLeads.size > 0 && <div className='partial-selection' /> }
                  </Form>
                </div>
                { columns?.map(column => (
                  <div key={column} className={column} style={{ flex: columnFlex(column) }}>
                    <SortableColumn type={column} selected={sortBy === column} sortDir={sortDir} onChangeSort={handleChangeSort} />
                  </div>
                ))}
                <div className='delete-lead'><EditColumnsDropdown categoryId={categoryId} show={showUserColumns} setShow={setShowUserColumns} /></div>
              </div>
              <div className='table-body'>
                { renderTableBody() }
              </div>
            </div>
          )}
          { leadsCount === 0 && !isLoading && <div className="empty-leads">{ renderEmptyTable() }</div> }
        </div>

        { selectedLeads?.size > 1 && <div className="selected-leads"><div><strong>{ selectedLeads.size }</strong> leads selected</div> <button onClick={() => setSelectedLeads(new Set())}>Clear Selection</button></div> }

        <LeadDetailsModal organizationId={organizationId} categoryId={categoryId} defaultStatus={category?.default_status_id} show={showDetails} lead={editingLead} onEditLead={onEditLead} onHide={onHideDetails} onDelete={() => handleDelete(leads.find(lead => lead.id === editingId))} />
        <ImportLeadsModal show={showImport} onHide={() => setShowImport(false)} />
        
        <ExportModal show={showExport} selectedLeads={selectedLeads} filters={filters} onHide={() => setShowExport(false)} category={category} />
        <BulkEditModal show={showBulkEdit} onHide={() => setShowBulkEdit(false)} type={bulkdEditType} onConfirm={onConfirmBulkEdit} ids={selectedLeads && Array.from(selectedLeads.keys())} />

        <Toast toast={toast} action={getToastAction()} onActionClick={onToastActionClick} onHide={hideToast} />
      </>
    )
  }

  return (
    <>
      <section className='__leads-list'>
        <LeadImportNotifications />
        <LeadsMode />
        { renderContent() }
      </section>

      <section className='__leads-list-mobile'>
        { renderMobileContent() }
      </section>
    </>
  )
}

const MoreToggle = React.forwardRef(({ children, onClick, ...props }, ref) => (
  <button onClick={onClick} ref={ref} {...props} className='main-actions more-actions'>
    { children }
  </button>
));

const FilterToggle = React.forwardRef(({ children, onClick, ...props }, ref) => (
  <button onClick={onClick} ref={ref} {...props} className='main-actions'>
    { children }
  </button>
));

const ActiveFilterToggle = React.forwardRef(({ children, onClick, ...props }, ref) => (
  <button onClick={onClick} ref={ref} {...props} className='main-actions active'>
    { children }
  </button>
));
